import { BarsArrowDownIcon } from "@heroicons/react/24/solid";
import {
    formatDateNoTime,
    fullSingleLineAddress,
    LatLng,
    ReferralMappings,
} from "@sp-crm/core";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { StatelessSearchbarItem } from "components/community-search/search-bar/stateless-searchbar-item";
import { AddressDisplay } from "components/contact-display/address-display";
import { EmailDisplay } from "components/contact-display/email-display";
import { FaxDisplay } from "components/contact-display/fax-display";
import { PhoneDisplay } from "components/contact-display/phone-display";
import { ReferenceTierField } from "components/references/show-reference/reference-tier-picker";
import { ClampedText } from "components/ui/clamped-text";
import { defaultLinkStyle } from "components/ui/link";
import {
    AdvancedSearchEntityType,
    ReferenceOrganizationSearchQuery,
} from "generated/graphql";
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useCanSeeReferralSourceOwners, usePreferences } from "store/selectors/hooks";
import { ReferenceDashboardAspect } from "store/slices/reference-dashboard";
import { pluralize } from "util/pluralize";
import { breakNewlines } from "util/text";
import {
    describeReferenceSort,
    ReferenceSearchSort,
    ReferenceSearchSortValues,
} from "./dashboard-reference/reference-search-sort";
import { ReferenceOrganizationSearchCardAnswers } from "./reference-organization-search-card-answers";

interface ReferenceOrganizationSearchListProps {
    organizationSearchResults: ReferenceOrganizationSearchQuery["referenceOrganizationSearch"]["hits"];
    setHoveredOrganization: (hoveredOrganization: HoveredMapEntity | null) => void;
    total: number;
    sortValues: ReferenceSearchSortValues;
    onSortChange: (
        aspect: ReferenceDashboardAspect,
        sort: string,
        sortDirection: "ASC" | "DESC",
    ) => void;
}

export const ReferenceOrganizationSearchList: React.FC<
    ReferenceOrganizationSearchListProps
> = props => {
    const {
        organizationSearchResults,
        setHoveredOrganization,
        total,
        sortValues,
        onSortChange,
    } = props;

    const handleEndHover = useCallback(() => {
        setHoveredOrganization(null);
    }, [setHoveredOrganization]);

    const currentSortDescription = useMemo(
        () => describeReferenceSort("organization", sortValues.organization),
        [sortValues.organization],
    );

    return (
        <>
            {total > 0 ? (
                <div className="flex items-center justify-between pb-2 lg:pb-4 pt-4 lg:pt-1">
                    <div className="text-sm font-bold flex-1">
                        {total} {pluralize(total, "organization", "organizations")} found
                    </div>
                    <StatelessSearchbarItem
                        content={
                            <div className="flex items-center space-x-2">
                                <BarsArrowDownIcon className="w-5 h-5 flex-shrink-0" />
                                <span className="inline md:inline text-sm">
                                    Sort: {currentSortDescription}
                                </span>
                            </div>
                        }>
                        <ReferenceSearchSort
                            aspect="organization"
                            values={sortValues}
                            onChange={onSortChange}
                        />
                    </StatelessSearchbarItem>
                </div>
            ) : null}
            <ul className="space-y-4">
                {organizationSearchResults.map(org => (
                    <li key={org.id}>
                        <ReferenceOrganizationSearchCard
                            business={org}
                            onHover={setHoveredOrganization}
                            onEndHover={handleEndHover}
                        />
                    </li>
                ))}
            </ul>
        </>
    );
};

interface ReferenceOrganizationSearchCardProps {
    business: ReferenceOrganizationSearchQuery["referenceOrganizationSearch"]["hits"][0];
    onHover: (hoveredOrganization: HoveredMapEntity | null) => void;
    onEndHover: () => void;
}

const showAllContactsThreshold = 50;

const ReferenceOrganizationSearchCard: React.FC<
    ReferenceOrganizationSearchCardProps
> = props => {
    const { business, onHover, onEndHover } = props;

    const { showReferralLastUpdatedDateOnMainTable } = usePreferences();
    const assignedToVisible = useCanSeeReferralSourceOwners();
    const [showAllContacts, setShowAllContacts] = React.useState(false);

    const orgTypeMapping = useMemo(() => {
        if (business.organizationType && ReferralMappings[business.organizationType]) {
            return ReferralMappings[business.organizationType];
        }

        return null;
    }, [business.organizationType]);

    const organizationAddress = fullSingleLineAddress({
        address: business.address,
        city: business.city,
        state: business.state,
        zip: business.zip,
    });

    const handleMouseEnter = () => {
        let location: LatLng | null = null;
        if (business.latitude && business.longitude) {
            location = {
                lat: parseFloat(business.latitude),
                lng: parseFloat(business.longitude),
            };
        }
        onHover({ entityId: business.id, location });
    };

    const handleMouseLeave = () => {
        onEndHover();
    };

    const toggleShowAllContacts = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setShowAllContacts(x => !x);
        },
        [],
    );

    const contacts = useMemo(() => {
        if (showAllContacts) {
            return business.contacts;
        }

        return business.contacts.slice(0, showAllContactsThreshold);
    }, [business.contacts, showAllContacts]);

    return (
        <div
            className="col-span-1 divide-y divide-gray-200 rounded md:rounded-lg bg-white shadow p-6"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <div className="flex-1 overflow-auto">
                <div className="flex space-x-2">
                    <div className="flex-1">
                        <h3 className="twoverride text-gray-900 text-lg">
                            {business.appLink ? (
                                <Link
                                    to={business.appLink}
                                    className="twoverride hover:underline">
                                    <div>{business.name || "(no name)"}</div>
                                </Link>
                            ) : null}
                        </h3>
                        {orgTypeMapping ? (
                            <div className="text-sm font-medium mb-1">
                                {orgTypeMapping.humanReadable}
                            </div>
                        ) : null}
                        {organizationAddress ? (
                            <div className="text-sm">
                                <AddressDisplay value={organizationAddress} />
                            </div>
                        ) : null}
                        <div className="text-sm">
                            {business.phone ? (
                                <PhoneDisplay
                                    value={business.phone}
                                    label="office"
                                    entityId={business.id}
                                    entityType={business.entityType}
                                />
                            ) : null}
                            {business.fax ? <FaxDisplay value={business.fax} /> : null}
                        </div>
                    </div>
                </div>
                <div className="mt-4 border-t border-gray-200 pt-2">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        {business.summary ? (
                            <div className="sm:col-span-2">
                                <dt className="text-sm font-medium text-gray-500">
                                    Summary
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    <ClampedText lines={3}>
                                        {breakNewlines(business.summary)}
                                    </ClampedText>
                                </dd>
                            </div>
                        ) : null}
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                                Number of referrals
                            </dt>
                            <dd className="mt-1 flex text-sm text-gray-900">
                                {business.numberOfReferrals}
                            </dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">
                                Most recent referral
                            </dt>
                            <dd className="mt-1 flex text-sm text-gray-900">
                                {formatDateNoTime(business.mostRecentReferral)}
                            </dd>
                        </div>
                        {showReferralLastUpdatedDateOnMainTable ? (
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">
                                    Last updated
                                </dt>
                                <dd className="mt-1 flex text-sm text-gray-900">
                                    {formatDateNoTime(business.updatedAt)}
                                </dd>
                            </div>
                        ) : null}
                        {assignedToVisible && business.assignedTo ? (
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">
                                    Assigned to
                                </dt>
                                <dd className="mt-1 flex text-sm text-gray-900">
                                    {business.assignedTo}
                                </dd>
                            </div>
                        ) : null}
                        <ReferenceTierField
                            sourceType={
                                business.entityType ===
                                AdvancedSearchEntityType.ReferenceBusiness
                                    ? "business"
                                    : "community"
                            }
                            sourceId={business.id}
                        />
                        <ReferenceOrganizationSearchCardAnswers organization={business} />
                        {contacts && contacts.length > 0 ? (
                            <div className="sm:col-span-2">
                                <dt className="text-sm font-medium text-gray-500">
                                    Contacts
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    <ul className="grid grid-cols-1 gap-x-4 gap-y-4 xl:grid-cols-2">
                                        {contacts.map((contact, i) => (
                                            <li key={i} className="space-y-0.5">
                                                <Link
                                                    className="twoverride hover:underline"
                                                    to={contact.appLink}>
                                                    <div className="font-semibold">
                                                        {contact.name || "(no name)"}
                                                    </div>
                                                </Link>
                                                {contact.role ? (
                                                    <div className="text-gray-700">
                                                        {contact.role}
                                                    </div>
                                                ) : null}
                                                {contact.email ? (
                                                    <EmailDisplay
                                                        value={contact.email}
                                                        label={null}
                                                        optOut={false}
                                                        entityType={business.entityType}
                                                        entityId={business.id}
                                                    />
                                                ) : null}
                                                {contact.cellPhone ? (
                                                    <PhoneDisplay
                                                        value={contact.cellPhone}
                                                        label="cell"
                                                        entityId={business.id}
                                                        entityType={business.entityType}
                                                    />
                                                ) : null}
                                                {contact.phone ? (
                                                    <PhoneDisplay
                                                        value={contact.phone}
                                                        label="other"
                                                        entityId={business.id}
                                                        entityType={business.entityType}
                                                    />
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                    {business.contacts.length >
                                    showAllContactsThreshold ? (
                                        <button
                                            className={`${defaultLinkStyle} text-sm text-left mt-4`}
                                            onClick={toggleShowAllContacts}>
                                            {showAllContacts
                                                ? "Show less"
                                                : "Show more contacts"}
                                        </button>
                                    ) : null}
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                </div>
            </div>
        </div>
    );
};
